import { useCreateUser } from '../../../../../hooks/actions/use-create-user'
import { UserInfo } from '../../../../../api/user/user-api-state';
import { selectUserId } from '../../../../../features/auth/auth-slice';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from '../../../../../assets/icon/svg';
import ConfirmDefaultModal from '../../../../global/modal/confirm-default-modal';
import getFullName from '../../../../../hooks/common/use-fullname';
import { useUserAction } from '../../../../../hooks/actions/use-user-action';
import { setConfirmTypeModal, setTyeOfStatus, setUserId } from '../../../../../features/gms-state-and-modal/modal-slice';
import { useToastify } from '../../../../../library/toastify/toast-provider';
import { useModalAccess } from '../../../../global/modal/use-modal-access';

interface Props {
    userData: UserInfo;
    actionType: 'info' | 'status'
}

export default function UserAction({ userData, actionType }: Props) {

    const { showToastNotify } = useToastify();
    const dispatch = useDispatch()
    const userName = userData
    ? getFullName({
        firstName: userData.firstname,
        lastName: userData.lastname,
        middleName: userData.middlename,
        suffix: userData?.suffix,
    })
    : '';
    const { 
        handleResetPassword, 
        handleDeactivateUser, 
        handleUpdateProfile, 
        handleCancelUpdate, 
        handleUpdateUser, 
        confirmAction, 
        setConfirmAction 
    } = useCreateUser();
    const {
        confirmModal,
        isResetPassword,
        isActivate
    } = useModalAccess()
    const { 
        handleCancel: handleCancelActions, 
        handleDeactivateUser: handleDeactivateUserAPI, 
        handleResetPassword: handleResetPasswordAPI 
    } = useUserAction()
    const currentId = useSelector(selectUserId);
    const { isLoadingUpdateUserAccountInfo, isErrorUpdateUserAccountInfo, } = useCreateUser()
    const isLoadingOrError = isLoadingUpdateUserAccountInfo || isErrorUpdateUserAccountInfo

    const handleCancel = () => {
        handleCancelActions();
        setConfirmAction(null);
    };

    const getConfirmationContent = () => {
        switch (confirmAction) {
            case 'update':
                return {
                    title: 'Update Profile',
                    subtitle: `Are you sure you want to update ${userName}'s profile?`,
                };
            case 'resetPassword':
                return {
                    title: 'Reset Password',
                    subtitle: `Are you sure you want to reset ${userName}'s password?`,
                };
            case 'activate':
                return {
                    title: 'Activate Account',
                    subtitle: `Are you sure you want to activate ${userName}'s account?`,
                };
            case 'deactivate':
                return {
                    title: 'Deactivate Account',
                    subtitle: `Are you sure you want to deactivate ${userName}'s account?`,
                };
            default:
                return {
                    title: '',
                    subtitle: '',
                };
        }
    };

    const confirmationContent = getConfirmationContent();

    return (
        <section className="overflow-hidden bg-white">
            <div className="flex flex-col-reverse gap-2 p-3 sm:flex-row sm:justify-end">
                {actionType === 'status' && (
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                const userId = userData.user_id
                                const action = userData.is_active ? 'deactivate' : 'activate'
                                if(!userId) return 
                                if(userId === currentId) {
                                    showToastNotify("You can't deactivate your own account.", 'warning');
                                    return;
                                }
                                dispatch(setUserId(userId))
                                dispatch(setConfirmTypeModal('isActivate'))
                                dispatch(setTyeOfStatus(action))
                                setConfirmAction(userData.is_active ? 'deactivate' : 'activate')
                            }}
                            disabled={userData.user_id === currentId}
                            className={`flex w-full items-center justify-center gap-1.5 rounded-lg border px-4 py-2 text-xs font-semibold shadow-sm transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto ${userData.is_active ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100' : 'border-green-200 bg-green-50 text-green-600 hover:bg-green-100'}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.84 2.73 17a2 2 0 0 0 1.73 3h15.08a2 2 0 0 0 1.73-3L13.7 3.84a2 2 0 0 0-3.4 0z" />
                            </svg>
                            <span>
                                {userData.is_active ? 'Deactivate Account' : 'Activate Account'}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const userId = userData.user_id
                                if (!userId) return;
                                if(userId === currentId) {
                                    showToastNotify("You can't reset your own password.", 'warning');
                                    return;
                                }
                                dispatch(setUserId(userId))
                                dispatch(setConfirmTypeModal('isResetPassword'))
                                setConfirmAction('resetPassword')
                            }}
                            disabled={userData.user_id === currentId}
                            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-[#186cb2]/30 hover:bg-[#186cb2]/5 hover:text-[#186cb2] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                                <rect width="18" height="11" x="3" y="11" rx="2" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            <span>
                                Reset Password
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleUpdateProfile(userData)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-[#186cb2]/20 transition hover:bg-[#155d9b] active:scale-[0.98] sm:w-auto"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487a2.1 2.1 0 0 1 2.97 2.97L8.25 19.04 4 20l.96-4.25L16.862 4.487Z" />
                            </svg>
                            <span>
                                Update Profile
                            </span>
                        </button>
                    </>
                )}
                {actionType === 'info' && (
                    <>
                        <button
                            type="button"
                            onClick={() => handleCancelUpdate(userData)}
                            disabled={isLoadingUpdateUserAccountInfo}
                            className={`flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[110px]`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                            <span>Cancel</span>
                        </button>
                        <button
                            type="button"
                            // onClick={handleUpdateUser}
                            onClick={() => {
                                dispatch(setConfirmTypeModal('isUpdateEmpProfile'))
                                setConfirmAction('update')
                            }}
                            disabled={isLoadingOrError}
                            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-[#186cb2]/20 transition hover:bg-[#155d9b] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[130px]"
                        >
                            {isLoadingUpdateUserAccountInfo ? (
                                <div className="flex w-full items-center justify-center gap-1.5">
                                    <Spinner size={14} />
                                    <span>Saving...</span>
                                </div>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5 9.5 17 19 7.5" />
                                    </svg>
                                    <span>Save Changes</span>
                                </>
                            )}
                        </button>
                    </>
                )}
            </div>
            {confirmModal && ['isResetPassword', 'isActivate', 'isUpdateEmpProfile'].includes(confirmModal ) && (
                <ConfirmDefaultModal
                    modalTitle={confirmationContent.title}
                    modalSubTitle={confirmationContent.subtitle}
                    onCancel={handleCancel}
                    onConfirm={ isResetPassword ? handleResetPasswordAPI : isActivate ? handleDeactivateUserAPI : handleUpdateUser }
                    isLoading={isLoadingUpdateUserAccountInfo}
                />
            )}
        </section>
    )
}